import clsx from "clsx";
import moment from "moment";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdCheck,
  MdClose,
  MdDoneAll,
  MdOutlinePriorityHigh,
} from "react-icons/md";
import CollapsibleBlock from "src/components/CollapsibleBlock/CollapsibleBlock";
import InfoBlock from "src/components/InfoBlock/InfoBlock";
import Modal from "src/components/Modal/Modal";
import { v4 as uuidv4 } from "uuid";

import CustomSelect, {
  ICustomSelectOption,
} from "src/components/Select/CustomSelect";
import TextInput from "src/components/TextInput/TextInput";
import { ETransactionType, ICON_SIZES } from "src/helpers/constants";
import {
  formatCurrency,
  formatFileSize,
  getTotalByTransactionType,
  showNotification,
  transformToSelectOptions,
} from "src/helpers/utils";
import useAccountLocalStorage from "src/hooks/useAccountLocalStorage";
import useCollapsibles, {
  EMoneyTransferMobileCollapsibles,
} from "src/hooks/useCollapsibles";
import useCurrentAccount from "src/hooks/useCurrentAccount";
import useCurrentUser from "src/hooks/useCurrentUser";
import useMainFilter from "src/hooks/useMainFilter";
import useMobile from "src/hooks/useMobile";
import MoneyTransferAttachments from "./MoneyTransferAttachments";
import { addTransaction } from "src/api/transactions";
import { TRANSACTION_STATUS_PAID } from "src/constants/transaction-status";
import useModalsStore from "src/store/modalsStore";
import useCache from "src/hooks/useCache";
import useDarkMode from "src/hooks/useDarkMode";
import TransactionAttachmentsLightbox from "./TransactionAttachmentsLightbox";
import ConfirmDialog from "src/components/ConfirmDialog/ConfirmDialog";
import { EButtonKind } from "src/components/Button/ButtonTypes";

interface IMoneyTransferModal {
  onClose: () => void;
  disableFields?: boolean;
  onSubmit: (values: any) => void;
}

const MoneyTransferModal: React.FC<IMoneyTransferModal> = ({
  onClose,
  disableFields = false,
  onSubmit,
}) => {
  const { clearCache } = useCache();
  const { setShowMoneyTransferModal } = useModalsStore();
  const { t } = useTranslation();
  const { allUserAccounts, getAccountById, allEnabledPaymentMethods } =
    useCurrentUser();
  const { accountLocalStoragePreferences, updateAccountLocalStorage } =
    useAccountLocalStorage();
  const { isEnabled: isDarkMode } = useDarkMode();
  const thumbnailsRef = useRef<any>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  const [attachmentsGalleryIsOpen, setAttachmentsGalleryIsOpen] =
    useState<boolean>(false);

  const {
    collapsiblePreferences,
    updateCollapsible,
    toggleMoneyTransferMobileCollapsible,
    isMoneyTransferMobileCollapsibleOpen,
  } = useCollapsibles();
  const [clickedAttachmentIndex, setClickedAttachmentIndex] =
    useState<number>(0);

  const {
    activeTransactions,
    currencySymbol,
    currentAccountData,
    currencyName,
    currentAccountId,
  } = useCurrentAccount();
  const [date, setDate] = useState<string>(moment().format("yyyy-MM-DD"));
  const [account, setAccount] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const { mainFilter } = useMainFilter();
  const [amountToTransfer, setAmountToTransfer] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>(
    accountLocalStoragePreferences.lastPaymentMethod
  );
  const { isMobile } = useMobile();
  const [receiptNumber, setReceiptNumber] = useState<string>("");
  const [filesToAttach, setFilesToAttach] = useState<Array<any>>([]);
  const [isTransferringMoney, setIsTransferringMoney] =
    useState<boolean>(false);

  const accountIncome = useMemo(
    () =>
      getTotalByTransactionType({
        type: ETransactionType.INCOME,
        transactions: activeTransactions,
      }),
    [activeTransactions]
  );

  const accountExpenses = useMemo(
    () =>
      getTotalByTransactionType({
        type: ETransactionType.EXPENSE,
        transactions: activeTransactions,
      }),
    [activeTransactions]
  );

  const paymentMethods = useMemo(() => {
    return transformToSelectOptions({
      data: allEnabledPaymentMethods,
      labelField: "name",
      valueField: "id",
    });
  }, [allEnabledPaymentMethods]);

  const totalAvailableMoney = useMemo(() => {
    return accountIncome - accountExpenses;
  }, [accountIncome, accountExpenses]);

  const dataIsValid = useMemo(() => {
    return !!date && !!account && !!paymentMethod && !!amountToTransfer;
  }, [date, account, amountToTransfer, paymentMethod]);

  const hasFunds = totalAvailableMoney > 0;

  const amountIsGreaterThanFunds = useMemo(() => {
    return Number(amountToTransfer) > totalAvailableMoney;
  }, [amountToTransfer, totalAvailableMoney]);

  const saveIsEnabled =
    totalAvailableMoney &&
    !!account &&
    !!amountToTransfer &&
    !!date &&
    !!paymentMethod &&
    !amountIsGreaterThanFunds;

  const transferMoney = async () => {
    setIsTransferringMoney(true);
    try {
      const transactionsToRegister = [
        {
          type: ETransactionType.EXPENSE,
          category: "Transferencia entre cuentas",
          account: currentAccountId,
          description: `Transacción a mi cuenta ${
            getAccountById(account)?.name || "Desconocida"
          }`,
          transactionId: uuidv4(),
        },
        {
          type: ETransactionType.INCOME,
          category: "Transferencia entre cuentas",
          account,
          description: `Transacción de mi cuenta ${
            currentAccountData?.name || "Desconocida"
          }`,
          transactionId: uuidv4(),
        },
      ];

      const results = await Promise.allSettled(
        transactionsToRegister?.map?.(
          async ({ category, type, account, description, transactionId }) => {
            return await addTransaction({
              transactionId,
              fieldsToSave: {
                invoice: transactionId,
                date,
                documentNumber: "",
                payTo: "Yo mismo",
                notes: notes?.trim?.() || "",
                taxes: 0,
                account,
                details: [],
                description,
                subTotal: amountToTransfer,
                type,
                discount: 0,
                category,
                status: TRANSACTION_STATUS_PAID,
                paymentMethod,
                total: amountToTransfer,
                tags: [],
              },
              files: filesToAttach,
            });
          }
        )
      );

      const successfulResults = results
        .filter((r) => r.status === "fulfilled")
        .map((r) => (r as PromiseFulfilledResult<any>).value.data);

      if (successfulResults?.length === transactionsToRegister?.length) {
        await clearCache();
        setShowConfirmDialog(false);
        setShowMoneyTransferModal(false);
        showNotification({
          message: "Fondos transferidos correctamente",
          kind: "success",
        });
      } else {
        showNotification({
          message: "Ocurrió un al transferir los fondos",
          kind: "error",
        });
      }
    } catch (e) {
      showNotification({
        message: "Ocurrió un al transferir los fondos",
        kind: "error",
      });
    } finally {
      setIsTransferringMoney(false);
    }
  };

  const unifiedAttachmentsList = useMemo(() => {
    return [
      ...(filesToAttach?.map?.((theFile: any) => {
        return {
          name: theFile.name,
          size: theFile.size,
          formattedSize: formatFileSize(theFile.size),
          type: theFile.type,
          typeFormatted:
            theFile?.type?.split?.("/")?.[1]?.toUpperCase?.() || "Desconocido",
          isSaved: false,
          thumbnailUrl: theFile?.preview,
          fileUrl: theFile?.preview,
        };
      }) || []),
    ];
  }, [filesToAttach]);

  const attachmentSlides: any[] = useMemo(() => {
    return (
      unifiedAttachmentsList?.map?.((attachment: any) => {
        return {
          type: attachment?.type === "application/pdf" ? undefined : "image",
          src: attachment?.fileUrl,
        };
      }) || []
    );
  }, [unifiedAttachmentsList]);

  return (
    <>
      <Modal
        onEscape={() => {
          if (!isTransferringMoney) {
            onClose();
          }
        }}
        className={clsx([
          "!w-full",
          {
            "md:!min-w-[800px] md:!max-w-[800px]": hasFunds,
            "md:!min-w-[500px] md:!max-w-[500px]": !hasFunds,
          },
        ])}
      >
        <Modal.Header
          title="Transferir fondos"
          onClose={() => {
            if (!isTransferringMoney) {
              onClose();
            }
          }}
        />
        <Modal.Body>
          <div
            className={clsx([
              "flex",
              "w-full",
              "flex-col",
              "gap-4",
              "md:gap-6",
              "text-sm",
              "md:text-base",
              "pb-2",
            ])}
          >
            <InfoBlock
              message={[
                `La cuenta destino debe estar en la misma moneda.`,
                `Automáticamente se crearán dos transacciones (una en cada cuenta) y`,
                "los archivos que adjuntes se agregarán a ambas.",
              ].join(" ")}
              displayMobileIcon
            />

            <div
              className={clsx([
                "flex",
                "w-full",
                "flex-row",
                "justify-start",
                "items-center",
                "gap-1",
              ])}
            >
              {hasFunds ? (
                <div className={clsx(["flex", "flex-col"])}>
                  <p>
                    Puedes transferir hasta &nbsp;
                    <strong className={clsx(["font-medium"])}>
                      {formatCurrency({
                        value: totalAvailableMoney,
                        currency: currencySymbol,
                      })}
                    </strong>
                    .
                  </p>
                  {amountIsGreaterThanFunds && (
                    <p className="warning-text">
                      El monto que intentas transferir es mayor al disponible.
                    </p>
                  )}
                </div>
              ) : (
                <p className={clsx(["warning-text"])}>
                  No tienes suficientes fondos para transferir a otra cuenta.
                </p>
              )}
            </div>

            {hasFunds && (
              <>
                <CollapsibleBlock
                  title="Datos"
                  uppercase
                  open={
                    isMobile
                      ? isMoneyTransferMobileCollapsibleOpen(
                          EMoneyTransferMobileCollapsibles.DATA
                        )
                      : collapsiblePreferences.moneyTransferDataOpen
                  }
                  onChange={(isOpen: boolean) => {
                    if (isMobile) {
                      toggleMoneyTransferMobileCollapsible(
                        EMoneyTransferMobileCollapsibles.DATA
                      );
                    } else {
                      updateCollapsible({ moneyTransferDataOpen: isOpen });
                    }
                  }}
                  badge={
                    dataIsValid
                      ? formatCurrency({
                          value: amountToTransfer,
                          currency: currencySymbol,
                        })
                      : "Incompleto"
                  }
                  icon={
                    dataIsValid && !amountIsGreaterThanFunds ? (
                      <MdDoneAll
                        size={ICON_SIZES.SM}
                        className="text-lime-400"
                      />
                    ) : (
                      <MdOutlinePriorityHigh
                        size={ICON_SIZES.SM}
                        className="text-red-500"
                      />
                    )
                  }
                >
                  <div
                    className={clsx([
                      "grid",
                      "grid-cols-1",
                      "md:grid-cols-3",
                      "gap-4",
                      "md:gap-6",
                    ])}
                  >
                    <TextInput
                      placeholder={t("transaction_page.date_placeholder")}
                      value={date}
                      onChange={(e) => setDate(e?.target?.value)}
                      label={t("transaction_page.date_label")}
                      type="date"
                      required
                      disabled={!hasFunds}
                    />
                    <TextInput
                      required
                      onChange={(e: any) =>
                        setAmountToTransfer(e?.target?.value)
                      }
                      value={amountToTransfer}
                      label="Monto"
                      placeholder="Digita el monto a transferir"
                      type="number"
                      disabled={!hasFunds}
                      autoComplete="off"
                    />
                    <CustomSelect
                      disabled={!hasFunds}
                      label="Cuenta destino"
                      options={
                        transformToSelectOptions({
                          data: allUserAccounts?.filter?.(
                            (account: any) =>
                              account?.account !== mainFilter?.account &&
                              account?.currency === currentAccountData?.currency
                          ),
                          labelField: "name",
                          valueField: "account",
                        }) as Array<ICustomSelectOption>
                      }
                      value={account}
                      useValueAsLabel={false}
                      onChange={(e: any) => setAccount(e?.value)}
                      required
                      placeholder="Selecciona una cuenta"
                    />
                    <CustomSelect
                      options={paymentMethods}
                      onChange={(e: any) => {
                        setPaymentMethod(e?.value);
                        updateAccountLocalStorage({
                          lastPaymentMethod: e?.value,
                        });
                      }}
                      label="Método de transferencia"
                      value={paymentMethod}
                      placeholder="Método de transferencia"
                      useValueAsLabel={false}
                      required
                      disabled={!hasFunds}
                    />
                    <TextInput
                      onChange={(e: any) => setReceiptNumber(e?.target?.value)}
                      label="Número de comprobante"
                      value={receiptNumber}
                      placeholder="Número de comprobante bancario"
                      disabled={!hasFunds}
                    />
                    <TextInput
                      onChange={(e: any) => setNotes(e?.target?.value)}
                      label="Nota"
                      value={notes}
                      placeholder="Digita una nota"
                      disabled={!hasFunds}
                    />
                  </div>
                </CollapsibleBlock>

                <MoneyTransferAttachments
                  attachments={[]}
                  onChange={({ unsaved }) => setFilesToAttach(unsaved)}
                  onClick={({
                    saved,
                    index,
                  }: {
                    saved: boolean;
                    index: number;
                  }) => {
                    setClickedAttachmentIndex(index);
                    setAttachmentsGalleryIsOpen(true);
                  }}
                />
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer
          actions={[
            {
              text: t("Transferir"),
              onClick: () => {
                setShowConfirmDialog(true);
              },
              kind: EButtonKind.SAVE,
              icon: <MdCheck size={ICON_SIZES.MD} />,
              isLoading: isTransferringMoney,
              disabled: !saveIsEnabled || isTransferringMoney,
              hide: !hasFunds,
            },
            {
              text: t("shared.close"),
              onClick: () => {
                if (!isTransferringMoney) {
                  onClose();
                }
              },
              kind: EButtonKind.NORMAL,
              icon: <MdClose size={ICON_SIZES.MD} />,
              disabled: isTransferringMoney,
            },
          ]}
        />
      </Modal>
      {attachmentsGalleryIsOpen && (
        <TransactionAttachmentsLightbox
          slides={attachmentSlides}
          index={clickedAttachmentIndex}
          setIndex={setClickedAttachmentIndex}
          thumbnailsRef={thumbnailsRef}
          unifiedAttachmentsList={unifiedAttachmentsList}
          onClose={() => setAttachmentsGalleryIsOpen(false)}
        />
      )}
      {showConfirmDialog && (
        <ConfirmDialog
          title="Transferir"
          description="¿Deseas transferir los fondos?"
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={async () => {
            await transferMoney();
          }}
          isLoading={isTransferringMoney}
        />
      )}
    </>
  );
};

export default MoneyTransferModal;
